import React, { useState } from 'react';
import { Award, BookOpen, MapPin, ChevronDown, ChevronUp, Calendar, GraduationCap, Tag } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useLanguage } from '../../contexts/LanguageContext';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const About = ({ personal, education, certifications }) => {
  const { t } = useLanguage();
  const [expandedEducation, setExpandedEducation] = useState({});
  const { ref: bioRef, isVisible: bioVisible } = useScrollReveal();

  // Helper for translated education text with fallback to raw data
  const et = (edu, field) => {
    if (!edu.translationKey) return edu[field];
    const key = `education.${edu.translationKey}.${field}`;
    const val = t(key);
    return val === key ? edu[field] : val;
  };
  const { ref: statsRef, isVisible: statsVisible } = useScrollReveal();
  const { ref: eduRef, isVisible: eduVisible } = useScrollReveal();
  const { ref: certRef, isVisible: certVisible } = useScrollReveal();

  const toggleEducation = (index) => {
    setExpandedEducation(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <section id="about" className="py-20 md:py-32 bg-background dark:bg-background">
      <div className="max-w-6xl mx-auto px-6 sm:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-sm font-medium mb-4 tracking-wide uppercase">
            {t('about.title')}
          </span>
          <h2 className="text-3xl md:text-5xl font-light text-foreground mb-4">
            {t('about.subtitle')}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-teal-500 mx-auto rounded-full" />
        </div>

        {/* Bio Section */}
        <div ref={bioRef} className={`max-w-3xl mx-auto text-center mb-16 reveal-up ${bioVisible ? 'visible' : ''}`}>
          <p className="text-lg text-muted-foreground leading-relaxed mb-6">
            {t('about.bio')}
          </p>
          <div className="flex items-center justify-center text-muted-foreground">
            <MapPin className="w-5 h-5 mr-2 text-teal-500" />
            <span>{personal.location}</span>
          </div>
        </div>

        {/* Stats Row */}
        <div ref={statsRef} className={`grid grid-cols-2 md:grid-cols-4 gap-4 mb-16 max-w-3xl mx-auto reveal-up ${statsVisible ? 'visible' : ''}`}>
          {[
            { value: '5+', label: t('about.yearsExperience') },
            { value: '10K+', label: t('about.usersImpacted') },
            { value: '10+', label: t('about.projectsCount') },
            { value: '6+', label: t('about.certifications') },
          ].map((stat, i) => (
            <div
              key={i}
              className="text-center p-6 bg-card dark:bg-card rounded-xl border border-border/50 hover:shadow-md hover:border-blue-200 dark:hover:border-blue-800 transition-all duration-300 relative overflow-hidden"
              style={{ animationDelay: `${i * 100}ms` }}
            >
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-600 to-teal-500" />
              <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent mb-1">
                {stat.value}
              </div>
              <div className="text-muted-foreground text-sm">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Education Section */}
        <div ref={eduRef} className={`mb-16 reveal-up ${eduVisible ? 'visible' : ''}`}>
          <div className="flex items-center justify-center mb-8">
            <div className="p-2 rounded-lg bg-blue-500/10 mr-3">
              <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-2xl font-semibold text-foreground">{t('education.title')}</h3>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {education.map((edu, index) => (
              <div
                key={index}
                className={`bg-card dark:bg-card rounded-xl p-6 border border-border/50 hover:shadow-lg hover:border-blue-200 dark:hover:border-blue-800 transition-all duration-300 border-l-4 ${
                  edu.status.includes('In Progress')
                    ? 'border-l-blue-500'
                    : 'border-l-green-500'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start flex-1">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center mr-3 flex-shrink-0">
                      <GraduationCap className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-foreground text-sm leading-tight mb-1">{et(edu, 'degree')}</h4>
                      <p className="text-muted-foreground text-xs">{et(edu, 'school')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <Badge
                      variant="secondary"
                      className={`text-xs whitespace-nowrap ${
                        edu.status.includes('In Progress')
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                          : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                      }`}
                    >
                      {edu.status.includes('In Progress') ? t('education.inProgress') : t('education.completed')}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleEducation(index)}
                      className="p-1 h-auto hover:bg-muted"
                    >
                      {expandedEducation[index] ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="flex items-center text-sm text-muted-foreground mb-2">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>{et(edu, 'year')}</span>
                  <span className="mx-2">•</span>
                  <span>{et(edu, 'focus')}</span>
                </div>

                {expandedEducation[index] && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-muted-foreground text-sm mb-4">{et(edu, 'description')}</p>
                    {edu.tags && edu.tags.length > 0 && (
                      <div>
                        <div className="flex items-center mb-2">
                          <Tag className="w-4 h-4 text-muted-foreground mr-2" />
                          <span className="text-xs font-medium text-foreground">{t('education.keyAreas')}:</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {edu.tags.map((tag, tagIndex) => (
                            <Badge
                              key={tagIndex}
                              variant="secondary"
                              className="text-xs bg-muted text-muted-foreground"
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Certifications Section */}
        <div ref={certRef} className={`reveal-up ${certVisible ? 'visible' : ''}`}>
          <div className="flex items-center justify-center mb-8">
            <div className="p-2 rounded-lg bg-green-500/10 mr-3">
              <Award className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-2xl font-semibold text-foreground">{t('about.certifications')}</h3>
          </div>

          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {certifications.map((cert, index) => (
              <Badge
                key={index}
                variant="secondary"
                className="py-2.5 px-5 bg-card dark:bg-card border border-border/50 text-foreground hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm transition-all duration-300 text-sm gap-2"
              >
                <Award className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                {cert}
              </Badge>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
